export type TeamLike = {
  id: string;
  coachUsernames?: Array<string | null> | null;
};

export type PlayerLike = {
  id: string;
  teamId: string;
};

export type TeamPlayerLike = {
  teamID: string;
  playerID: string;
};

export type RosterMembershipLike = {
  id: string;
  playerId: string;
  teamId: string;
  relationship?: string | null;
  isPrimary?: boolean | null;
};

export type UserAssignmentLike = {
  id: string;
  userId: string;
  teamId?: string | null;
  organizationId?: string | null;
  leagueId?: string | null;
  programId?: string | null;
  playerId?: string | null;
  relationship?: string | null;
};

const coachLikeRelationships = ["coach", "commish", "superadmin", "assistant"];

const isCoachLikeAssignment = (assignment: UserAssignmentLike): boolean => {
  if (!assignment.teamId) {
    return false;
  }
  const relationship = (assignment.relationship ?? "").trim().toLowerCase();
  if (!relationship) {
    return !assignment.playerId;
  }
  return coachLikeRelationships.some((keyword) => relationship.includes(keyword));
};

export const buildPlayerTeamIds = ({
  players = [],
  teamPlayers = [],
  rosterMemberships = []
}: {
  players?: PlayerLike[];
  teamPlayers?: TeamPlayerLike[];
  rosterMemberships?: RosterMembershipLike[];
}): Record<string, Set<string>> => {
  const out: Record<string, Set<string>> = {};
  for (const player of players) {
    out[player.id] = new Set<string>([player.teamId]);
  }
  for (const link of teamPlayers) {
    if (!out[link.playerID]) {
      out[link.playerID] = new Set<string>();
    }
    out[link.playerID].add(link.teamID);
  }
  for (const membership of rosterMemberships) {
    if (!out[membership.playerId]) {
      out[membership.playerId] = new Set<string>();
    }
    out[membership.playerId].add(membership.teamId);
  }
  return out;
};

export const buildCoachTeamIds = ({
  username,
  teams = [],
  userAssignments = []
}: {
  username: string;
  teams?: TeamLike[];
  userAssignments?: UserAssignmentLike[];
}): Set<string> => {
  const out = new Set<string>();
  for (const team of teams) {
    if ((team.coachUsernames ?? []).includes(username)) {
      out.add(team.id);
    }
  }
  for (const assignment of userAssignments) {
    if (assignment.userId !== username || !assignment.teamId) {
      continue;
    }
    if (isCoachLikeAssignment(assignment)) {
      out.add(assignment.teamId);
    }
  }
  return out;
};

export const getTeamCoachUsernames = ({
  team,
  userAssignments = []
}: {
  team: TeamLike;
  userAssignments?: UserAssignmentLike[];
}): string[] => {
  const usernames = new Set<string>((team.coachUsernames ?? []).filter((value): value is string => Boolean(value)));
  for (const assignment of userAssignments) {
    if (assignment.teamId === team.id && isCoachLikeAssignment(assignment)) {
      usernames.add(assignment.userId);
    }
  }
  return [...usernames];
};
